/**
 * Route initialization utilities.
 *
 * This module provides:
 * 1. `initializeForRoute` - A function to be called during app initialization
 *    to set up the correct farm context based on the current route.
 * 2. `useRouteInitialization` - A hook for handling route-based initialization
 *    after the initial app setup.
 *
 * This replaces the pathname string matching previously done in PrivateRoute.
 */

import { useIsMobile } from "common/utils";
import { Farm } from "model";
import { useEffect, useRef } from "react";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import {
  fetchAnimalGroupsById,
  fetchFarmById,
  fetchFieldsByIdWithResult,
  finishLoadingState,
  initializeFarm,
} from "services";
import { findRouteByPath, getDefaultRoute } from "../routes";

/**
 * Options for the initializeForRoute function
 */
interface InitializeForRouteOptions {
  /** Current pathname */
  pathname: string;
  /** User's farms from initializeApp */
  farms: Farm[];
  /** Whether this is a corporate user */
  isCorporateUser: boolean;
  /** Whether the user is on mobile */
  isMobile: boolean;
  /** React Router navigate function */
  navigate: NavigateFunction;
  /** Whether farm initialization is needed (from initializeApp) */
  needsFarmInit?: boolean;
}

/**
 * Initialize farm context based on the current route.
 *
 * This function is called during the initial app setup (after token refresh)
 * to determine which farm to initialize based on the current URL.
 *
 * Uses the route config's `initializeWith` property instead of
 * pathname string matching.
 *
 * @returns true if navigation was performed (caller should stop further processing)
 */
export async function initializeForRoute({
  pathname,
  farms,
  isCorporateUser,
  isMobile,
  navigate,
  needsFarmInit = true,
}: InitializeForRouteOptions): Promise<boolean> {
  const route = findRouteByPath(pathname);
  const initializeWith = route?.initializeWith ?? "defaultFarm";
  const userType = isCorporateUser ? "corporate" : "farmer";

  // If app init indicated no farm init is needed (e.g., corporate user),
  // just finish loading state and return
  if (!needsFarmInit && isCorporateUser) {
    finishLoadingState();
    return false;
  }

  // Helper to redirect to default view
  const redirectToDefault = async (): Promise<boolean> => {
    const defaultFarm = farms[0];
    if (defaultFarm) {
      await initializeFarm(defaultFarm);
      if (isMobile) {
        navigate(`/farm/${defaultFarm.guid}`);
      } else {
        navigate(getDefaultRoute(userType, defaultFarm.guid));
      }
      return true;
    } else {
      finishLoadingState();
      navigate("/add-farm/enter-name");
      return true;
    }
  };

  // Corporate users: handle routes that don't require farm initialization
  if (isCorporateUser) {
    // Routes with initializeWith: "none" don't need any initialization
    if (initializeWith === "none") {
      finishLoadingState();
      return false; // Don't redirect, stay on current route
    }

    // Routes that require farm initialization but corporate users can access (userType: "all")
    // Corporate users don't have farms, but they can still access shared routes like file-management
    if (route && (route.userType === "all" || route.userType === "corporate")) {
      // Allow corporate users to stay on routes they can access, even if initializeWith requires a farm
      // The component itself should handle the case where there's no farm
      finishLoadingState();
      return false; // Don't redirect, stay on current route
    }

    // If it's a route that requires farm initialization and corporate users can't access it,
    // redirect to default (this shouldn't happen for valid routes, but handle it gracefully)
    if (initializeWith !== "none" && route && route.userType === "farmer") {
      finishLoadingState();
      navigate(getDefaultRoute("corporate"));
      return true;
    }

    // Default: finish loading without redirect
    finishLoadingState();
    return false;
  }

  // Handle routes that don't need farm context
  if (initializeWith === "none") {
    finishLoadingState();
    return false;
  }

  // No farms - redirect to add farm
  if (!farms.length && initializeWith !== "addFarm") {
    finishLoadingState();
    navigate("/add-farm/enter-name");
    return true;
  }

  try {
    switch (initializeWith) {
      case "farm": {
        // Initialize by farm ID from URL
        const farmId = pathname.split("/")[2];
        if (!farmId) {
          return await redirectToDefault();
        }
        try {
          const farm = await fetchFarmById(farmId);
          await initializeFarm(farm);
          return false;
        } catch {
          return await redirectToDefault();
        }
      }

      case "field": {
        // Initialize by field ID - fetch field, then its farm
        const fieldId = pathname.split("/")[2];
        if (!fieldId) {
          return await redirectToDefault();
        }
        try {
          const fields = await fetchFieldsByIdWithResult([fieldId]);
          if (fields.length === 1) {
            const farm = await fetchFarmById(fields[0].farm);
            await initializeFarm(farm);
            return false;
          } else {
            console.log("found more than 1 field for the same guid");
            return await redirectToDefault();
          }
        } catch {
          return await redirectToDefault();
        }
      }

      case "animalGroup": {
        // Initialize by animal group ID - fetch group, then its farm
        const animalGroupId = pathname.split("/")[2];
        if (!animalGroupId) {
          return await redirectToDefault();
        }
        try {
          const animalGroups = await fetchAnimalGroupsById([animalGroupId]);
          if (animalGroups[0]) {
            const farm = await fetchFarmById(animalGroups[0].farm);
            await initializeFarm(farm);
            return false;
          } else {
            return await redirectToDefault();
          }
        } catch {
          return await redirectToDefault();
        }
      }

      case "addFarm": {
        // Special handling for add-farm route
        if (farms.length > 0) {
          // User has farms but is on add-farm page, initialize a default farm for context
          const defaultFarm = farms[0];
          if (defaultFarm) {
            await initializeFarm(defaultFarm);
          }
        } else {
          finishLoadingState();
        }
        return false;
      }

      case "defaultFarm":
      default: {
        // Initialize with the user's first farm
        const defaultFarm = farms[0];
        if (defaultFarm) {
          await initializeFarm(defaultFarm);
          return false;
        }
        return await redirectToDefault();
      }
    }
  } catch {
    return await redirectToDefault();
  }
}

interface UseRouteInitializationOptions {
  /** User's farms from the initial app initialization */
  farms: Farm[];
  /** Whether this is a corporate user */
  isCorporateUser: boolean;
  /** The current pathname */
  pathname: string;
}

interface UseRouteInitializationResult {
  /** Whether initialization is in progress */
  isInitializing: boolean;
}

/**
 * Hook to initialize farm context based on the current route.
 *
 * This hook:
 * 1. Looks up the route config for the current pathname
 * 2. Based on the route's `initializeWith` property, fetches and initializes the appropriate data
 * 3. Handles redirects for routes that can't be initialized (invalid IDs, etc.)
 */
export function useRouteInitialization({
  farms,
  isCorporateUser,
  pathname,
}: UseRouteInitializationOptions): UseRouteInitializationResult {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const params =
    useParams<{ farmId?: string; fieldId?: string; animalGroupId?: string }>();

  const isInitializingRef = useRef(false);
  const lastPathnameRef = useRef<string>("");

  useEffect(() => {
    // Skip if already initializing or if pathname hasn't changed
    if (isInitializingRef.current || lastPathnameRef.current === pathname) {
      return;
    }

    const route = findRouteByPath(pathname);
    const initializeWith = route?.initializeWith ?? "defaultFarm";
    const userType = isCorporateUser ? "corporate" : "farmer";

    // Helper to redirect to default view
    const redirectToDefault = async () => {
      const defaultFarm = farms[0];
      if (defaultFarm) {
        await initializeFarm(defaultFarm);
        if (isMobile) {
          navigate(`/farm/${defaultFarm.guid}`);
        } else {
          navigate(getDefaultRoute(userType, defaultFarm.guid));
        }
      } else {
        finishLoadingState();
        navigate("/add-farm/enter-name");
      }
    };

    const doInitialize = async () => {
      isInitializingRef.current = true;
      lastPathnameRef.current = pathname;

      try {
        switch (initializeWith) {
          case "none":
            // No initialization needed (oauth, special routes)
            finishLoadingState();
            break;

          case "farm": {
            // Initialize by farm ID from route params
            const farmId = params.farmId || pathname.split("/")[2];
            if (!farmId || !farms.length) {
              await redirectToDefault();
              return;
            }
            try {
              const farm = await fetchFarmById(farmId);
              await initializeFarm(farm);
            } catch {
              await redirectToDefault();
            }
            break;
          }

          case "field": {
            // Initialize by field ID - fetch field, then its farm
            const fieldId = params.fieldId || pathname.split("/")[2];
            if (!fieldId || !farms.length) {
              await redirectToDefault();
              return;
            }
            try {
              const fields = await fetchFieldsByIdWithResult([fieldId]);
              if (fields.length === 1) {
                const farm = await fetchFarmById(fields[0].farm);
                await initializeFarm(farm);
              } else {
                console.log("found more than 1 field for the same guid");
                await redirectToDefault();
              }
            } catch {
              await redirectToDefault();
            }
            break;
          }

          case "animalGroup": {
            // Initialize by animal group ID - fetch group, then its farm
            const animalGroupId =
              params.animalGroupId || pathname.split("/")[2];
            if (!animalGroupId || !farms.length) {
              await redirectToDefault();
              return;
            }
            try {
              const animalGroups = await fetchAnimalGroupsById([animalGroupId]);
              if (animalGroups[0]) {
                const farm = await fetchFarmById(animalGroups[0].farm);
                await initializeFarm(farm);
              } else {
                await redirectToDefault();
              }
            } catch {
              await redirectToDefault();
            }
            break;
          }

          case "addFarm": {
            // Special handling for add-farm route
            if (farms.length > 0) {
              // User has farms but is on add-farm page, initialize a default farm for context
              const defaultFarm = farms[0];
              if (defaultFarm) {
                await initializeFarm(defaultFarm);
              }
            } else {
              finishLoadingState();
            }
            break;
          }

          case "defaultFarm":
          default: {
            // Initialize with the user's first farm
            if (!farms.length) {
              finishLoadingState();
              navigate("/add-farm/enter-name");
              return;
            }
            const defaultFarm = farms[0];
            if (defaultFarm) {
              await initializeFarm(defaultFarm);
            }
            break;
          }
        }
      } finally {
        isInitializingRef.current = false;
      }
    };

    // Only run initialization for farmer users with farms (or specific routes)
    if (isCorporateUser) {
      finishLoadingState();
    } else if (
      farms.length > 0 ||
      initializeWith === "none" ||
      initializeWith === "addFarm"
    ) {
      (async () => {
        await doInitialize();
      })().catch((error) => {
        console.error("Failed to initialize route", error);
      });
    } else {
      finishLoadingState();
      navigate("/add-farm/enter-name");
    }
  }, [pathname, farms, isCorporateUser, params, navigate, isMobile]);

  return {
    isInitializing: isInitializingRef.current,
  };
}
